import { Update, UpdateChannel } from '../types';
import i18n from '../i18n/config';
import { logger } from '../utils/logger';

/**
 * Updates-service — productnieuws / changelog.
 *
 * De feed wordt live geleverd door de portal-backend (portal.notizy.nl), gefilterd
 * op kanaal én taal. Zie docs/updates-feed-api.md voor het volledige contract.
 *
 * De portal-feed is een vaste, centrale en publieke bron (los van de instelbare
 * Notizy-backend per tenant); daarom een eigen base-URL met env-override voor dev.
 */

const API_BASE = (process.env.REACT_APP_PORTAL_URL || 'https://portal.notizy.nl').replace(/\/$/, '');

/** Het kanaal van deze applicatie; bepaalt welke updates zichtbaar zijn. */
const CURRENT_CHANNEL: UpdateChannel = 'web';

/**
 * Actieve taal als kale taalcode (bijv. 'en-US' → 'en') voor de feed. Valt terug
 * op de i18n-fallback ('nl') zolang i18n nog niet geïnitialiseerd is.
 */
function currentLanguage(): string {
    const lang = i18n.resolvedLanguage || i18n.language || 'nl';
    return lang.split('-')[0];
}

function endpoint(path: string): string {
    const sep = path.includes('?') ? '&' : '?';
    return `${API_BASE}/api/updates${path}${sep}channel=${CURRENT_CHANNEL}&language=${currentLanguage()}`;
}

/** Defensieve sortering (nieuwste eerst) — de API levert deze volgorde al. */
function sortByDateDesc(updates: Update[]): Update[] {
    return [...updates].sort(
        (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
    );
}

class UpdatesService {
    /** Updates voor dit kanaal, nieuwste eerst. Bij fouten: lege lijst. */
    async getUpdates(): Promise<Update[]> {
        try {
            const res = await fetch(endpoint(''));
            if (!res.ok) return [];
            const data = (await res.json()) as Update[];
            return sortByDateDesc(Array.isArray(data) ? data : []);
        } catch (err) {
            logger.error('Fout bij het laden van updates:', err);
            return [];
        }
    }

    /** Het uitgelichte bericht voor de frontpage (of null). */
    async getFeaturedUpdate(): Promise<Update | null> {
        try {
            const res = await fetch(endpoint('/featured'));
            // 204 = geen uitgelicht bericht voor dit kanaal
            if (res.status === 204 || !res.ok) return null;
            return (await res.json()) as Update;
        } catch (err) {
            logger.error('Fout bij het laden van het uitgelichte bericht:', err);
            return null;
        }
    }

    /** Eén update op basis van zijn slug — null bij 404 (onbekend/off-channel). */
    async getUpdateBySlug(slug: string): Promise<Update | null> {
        try {
            const res = await fetch(endpoint(`/${encodeURIComponent(slug)}`));
            if (res.status === 404 || !res.ok) return null;
            return (await res.json()) as Update;
        } catch (err) {
            logger.error('Fout bij het laden van de update:', err);
            return null;
        }
    }
}

const updatesService = new UpdatesService();
export default updatesService;
